"use client";

import React from "react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Loader, CircleCheck, FlaskConical, User, Settings2 } from "lucide-react";
export default function RequestABoilerRepair() {
  return <section data-section-id="223198" id="request-a-boiler-repair" className="py-24 bg-background text-foreground relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-[20%] -right-[10%] w-[50%] h-[50%] rounded-lg bg-primary/10 blur-3xl opacity-50" />
        <div className="absolute bottom-[10%] -left-[10%] w-[40%] h-[40%] rounded-lg bg-accent/20 blur-3xl opacity-50" />
      </div>

      <div className="container mx-auto px-4 relative z-10 max-w-4xl">
        <div className="text-center mb-12">
          <h2 className="text-3xl md:text-4xl font-default font-bold text-foreground mb-4 tracking-tight">
            Request a Boiler Repair
          </h2>
          <p className="text-muted-foreground max-w-2xl mx-auto font-default text-lg">
            Experiencing issues with your heating? Fill out the form below for a fast response from our RGI registered engineers. For emergencies, please call 01 908 1819 or WhatsApp 085 706 3869.
          </p>
        </div>

        <FormProvider formKey="request_form_boiler_repair" formId="request_form_boiler_repair" sectionName="Boiler Repair Request" className="bg-card border border-border shadow-xl shadow-shadow-color/10 rounded-2xl p-6 md:p-10" defaultValues={{
        name: "",
        email: "",
        organization: "",
        peptide_sequence: "",
        quantity_mg: "",
        purity: "Ideal",
        additional_details: ""
      }} validationRules={{
        name: {
          required: "Name is required"
        },
        email: {
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        },
        organization: {},
        peptide_sequence: {
          required: "Fault description is required"
        },
        quantity_mg: {
          required: "Phone number is required"
        },
        purity: {
          required: "Boiler brand is required"
        },
        additional_details: {}
      }} submitText="Request Repair Quote" successMessage="Your repair request has been received. A D4 Plumbing & Heating specialist will contact you shortly to confirm your booking.">
          <InnerSynthesisForm />
        </FormProvider>
      </div>
    </section>;
}
function InnerSynthesisForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-16 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-20 rounded-lg bg-accent/20 flex items-center justify-center mb-4">
          <CircleCheck className="size-10 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Request Received</h3>
        <p className="text-muted-foreground max-w-md font-default">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary text-primary hover:bg-primary/5">
          Submit Another Request
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-10">
      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div className="bg-primary/10 p-2 rounded-md">
            <User className="size-5 text-primary" />
          </div>
          <h3 className="text-lg font-default font-bold text-foreground uppercase tracking-wider">01. Contact Information</h3>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="name" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default font-semibold">Full Name <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input placeholder="John Murphy" className="bg-background border-2 focus-visible:ring-ring" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="email" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default font-semibold">Email Address <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="email" placeholder="john@example.ie" className="bg-background border-2 focus-visible:ring-ring" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="organization" render={({
          field
        }) => <FormItem className="md:col-span-2">
                <FormLabel className="font-default font-semibold">Property Address / Eircode</FormLabel>
                <FormControl>
                  <Input placeholder="123 Dublin Road, D04..." className="bg-background border-2 focus-visible:ring-ring" {...field} />
                </FormControl>
                <FormMessage />
              </FormItem>} />
        </div>
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div className="bg-primary/10 p-2 rounded-md">
            <FlaskConical className="size-5 text-primary" />
          </div>
          <h3 className="text-lg font-default font-bold text-foreground uppercase tracking-wider">02. Fault Details</h3>
        </div>

        <FormField control={control} name="peptide_sequence" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold">Describe the Issue <span className="text-destructive">*</span></FormLabel>
              <FormControl>
                <Textarea placeholder="e.g., Boiler making banging noise, no hot water, or error code E110..." className="min-h-[120px] font-default text-base bg-background border-2 focus-visible:ring-ring resize-y" {...field} />
              </FormControl>
              <FormDescription className="font-mono text-[10px] uppercase text-muted-foreground">
                Please include any error codes displayed on the boiler panel.
              </FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      {}
      <div className="space-y-6">
        <div className="flex items-center gap-3 border-b border-border pb-4">
          <div className="bg-primary/10 p-2 rounded-md">
            <Settings2 className="size-5 text-primary" />
          </div>
          <h3 className="text-lg font-default font-bold text-foreground uppercase tracking-wider">03. Boiler Specifications</h3>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <FormField control={control} name="quantity_mg" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default font-semibold">Phone Number <span className="text-destructive">*</span></FormLabel>
                <FormControl>
                  <Input type="tel" placeholder="085 706 3869" className="bg-background border-2 focus-visible:ring-ring" {...field} />
                </FormControl>
                <FormDescription className="font-mono text-[10px] uppercase text-muted-foreground">
                  We will call you to schedule the repair.
                </FormDescription>
                <FormMessage />
              </FormItem>} />

          <FormField control={control} name="purity" render={({
          field
        }) => <FormItem>
                <FormLabel className="font-default font-semibold">Boiler Brand <span className="text-destructive">*</span></FormLabel>
                <Select onValueChange={field.onChange} defaultValue={field.value}>
                  <FormControl>
                    <SelectTrigger className="bg-background border-2 focus-visible:ring-ring">
                      <SelectValue placeholder="Select brand" />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    <SelectItem value="Viessmann">Viessmann</SelectItem>
                    <SelectItem value="Ideal">Ideal</SelectItem>
                    <SelectItem value="Grant">Grant</SelectItem>
                    <SelectItem value="Worcester Bosch">Worcester Bosch</SelectItem>
                    <SelectItem value="Other">Other / Not Sure</SelectItem>
                  </SelectContent>
                </Select>
                <FormMessage />
              </FormItem>} />
        </div>

        <FormField control={control} name="additional_details" render={({
        field
      }) => <FormItem>
              <FormLabel className="font-default font-semibold">Urgency / Preferred Time</FormLabel>
              <FormControl>
                <Textarea placeholder="Is this an emergency? Preferred morning or afternoon visit?" className="min-h-[100px] bg-background border-2 focus-visible:ring-ring resize-y" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>} />
      </div>

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-lg flex items-start gap-3">
          <div className="text-destructive mt-0.5"></div>
          <p className="text-destructive text-sm font-default">{submitError}</p>
        </div>}

      <div className="pt-6 border-t border-border">
        <Button type="submit" disabled={isSubmitting} size="lg" className="w-full md:w-auto md:min-w-[240px] font-default font-bold tracking-wide bg-primary hover:bg-primary/90 text-primary-foreground shadow-lg shadow-primary/20">
          {isSubmitting ? <>
              <Loader className="animate-spin mr-2 size-5" />
              Sending Request...
            </> : 'Request Repair Quote'}
        </Button>
      </div>
    </Form>;
}